"use client";

import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import styles from "./lpMoov.module.css";

interface MoovLpRevealProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    delay?: number;
    instant?: boolean;
    stagger?: boolean;
}

export default function MoovLpReveal({
    children,
    className = "",
    delay = 0,
    instant = false,
    stagger = false,
    style,
    ...rest
}: MoovLpRevealProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(instant);

    useEffect(() => {
        if (instant) return;

        const node = ref.current;
        if (!node) return;

        if (node.getBoundingClientRect().top < window.innerHeight - 48) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Initial visibility comes from the DOM ref; preserve immediate reveal for already-visible content.
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [instant]);

    return (
        <div
            ref={ref}
            className={[
                styles.reveal,
                stagger ? styles.stagger : styles.revealBlock,
                visible ? styles.revealIn : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            style={{ "--reveal-delay": `${delay}ms`, ...style } as CSSProperties}
            {...rest}
        >
            {children}
        </div>
    );
}
